// Teams identity helper for the integration/* chat path (legacy x-user-id flow).
//
// Resolution order (mirrors feature/agent-users TeamsOnlyGate behavior):
//   1. @microsoft/teams-js SDK — when running inside a Teams tab the SDK
//      returns the real user UUID via app.getContext().user.id. This is
//      what AgentCore's search_knowledge tool requires.
//   2. localStorage 'ct-user-id' — dev/browser-test shim. Set via:
//      localStorage.setItem('ct-user-id', '<uuid>')
//   3. Stable anonymous fallback — keeps the API call going so the UI
//      doesn't hard-fail; AgentCore tools that gate on real users will
//      degrade gracefully.

import { app } from '@microsoft/teams-js';

import { randomUuid } from '../../lib/random-uuid.js';

const STORAGE_KEY = 'ct-user-id';
const TEAMS_TIMEOUT_MS = 3000;

let cachedUserId: string | null = null;
let cachedUserEmail: string | null = null;
let resolveOnce: Promise<string> | null = null;

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${ms}ms`)),
      ms,
    );
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(timer);
        reject(e instanceof Error ? e : new Error(String(e)));
      },
    );
  });
}

async function resolveFromTeams(): Promise<{
  id: string;
  email?: string;
} | null> {
  try {
    await withTimeout(app.initialize(), TEAMS_TIMEOUT_MS, 'app.initialize');
    const ctx = await withTimeout(
      app.getContext(),
      TEAMS_TIMEOUT_MS,
      'app.getContext',
    );
    const id = ctx.user?.id;
    if (!id) return null;
    return { id, email: ctx.user?.userPrincipalName };
  } catch {
    return null;
  }
}

async function resolve(): Promise<string> {
  const teams = await resolveFromTeams();
  if (teams) {
    cachedUserId = teams.id;
    cachedUserEmail = teams.email ?? null;
    return teams.id;
  }
  const envOid = import.meta.env['VITE_DEV_USER_OID'] as string | undefined;
  if (envOid && envOid !== 'REPLACE_ME') {
    cachedUserId = envOid;
    const envUpn = import.meta.env['VITE_DEV_USER_UPN'] as string | undefined;
    if (envUpn && envUpn !== 'REPLACE_ME') {
      cachedUserEmail = envUpn;
    }
    return envOid;
  }
  const stored =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem(STORAGE_KEY)
      : null;
  if (stored) {
    cachedUserId = stored;
    return stored;
  }
  const fallback = randomUuid();
  cachedUserId = fallback;
  return fallback;
}

export async function awaitTeamsUserId(): Promise<string> {
  if (cachedUserId) return cachedUserId;
  if (!resolveOnce) resolveOnce = resolve();
  return resolveOnce;
}

export function getTeamsUserEmail(): string | null {
  return cachedUserEmail;
}
